import { get, isEmpty } from 'lodash';
import { notification } from 'antd';
import {
  qryPaymentMethod,
  paymentOrder,
  qryPaymentConfirm,
  qryConfigParams,
  genCaptcha,
  validCaptcha,
  qryDue,
} from '@pages/OnlinePayment/PaymentMethod/services';
import { Base64 } from 'js-base64';
import { DRM_DEF_CURRENCY_PRECISION, DRM_CURRENCY_SHOW_PRECISION, getRequestData } from '@/utils';

const initState = {
  paymentMethodList: [],
  selectBankInfo: [],
  selectPaymentMethodInfo: [],
  transTableInfo: [],
  configParams: {},
  imgReturn: {}, // 图形验证码
  refreshState: false, // 根据paymentConfirm结果判断是否刷新
  billingInfo: {},
};

export default {
  namespace: 'onlinePayment',
  state: {
    ...initState,
  },
  effects: {
    // 获取访问组记录history
    *qryPaymentMethod({ payload }, { call, put }) {
      const { paymentType } = payload;
      const encryptParams = {
        url: '/whalecloud/cvbs/v1',
        belong: 'ocs',
        data: Base64.encode(JSON.stringify(payload)),
      };
      let result = yield call(qryPaymentMethod, encryptParams);
      result = getRequestData(result);
      if (Base64.isValid(result)) {
        result = JSON.parse(Base64.decode(result));
      }
      let paymentMethodList = get(result, 'PaymentModeList') || [];
      if (!isEmpty(get(result, 'ApiResponse'))) {
        notification.error({
          message: 'Error',
          description: get(result, 'ApiResponse.resultDesc'),
        });
        return false;
      }
      if (!isEmpty(result)) {
        paymentMethodList = paymentMethodList.map(item => {
          const showBankList = (get(item, 'OnlineBankList') || []).filter(v => v.OtherBankFlag === 'N');
          const hideBankList = (get(item, 'OnlineBankList') || []).filter(v => v.OtherBankFlag === 'Y');
          return {
            ...item,
            showBankList,
            hideBankList,
          };
        });
        // 3062365 Transaction only should be done from UPI
        if (paymentType === 'Drm') {
          paymentMethodList = paymentMethodList.filter(item => item.PaymentModeName === 'UPI ACCOUNT');
        }
        yield put({
          type: 'save',
          payload: {
            paymentMethodList,
          },
        });
        return paymentMethodList;
      }
      return true;
    },
    *qryPaymentOrder({ payload }, { call }) {
      const encryptParams = {
        url: '/whalecloud/cvbs/v1',
        belong: 'ocs',
        data: Base64.encode(JSON.stringify(payload)),
      };
      let result = yield call(paymentOrder, encryptParams);
      result = getRequestData(result);
      if (Base64.isValid(result)) {
        result = JSON.parse(Base64.decode(result));
      }
      if (!isEmpty(get(result, 'ApiResponse'))) {
        if (get(result, 'ApiResponse.resultCode') === 'BL-S-ACT-00937') {
          return { refreshState: true };
        }
        notification.error({
          message: 'Error',
          description: get(result, 'ApiResponse.resultDesc'),
        });
        return {};
      }
      if (!isEmpty(result)) {
        return result;
      }
      return {};
    },
    *qryPaymentConfirm({ payload }, { call, put }) {
      const paramCodes = `${DRM_DEF_CURRENCY_PRECISION},${DRM_CURRENCY_SHOW_PRECISION}`;
      yield put({
        type: 'qryConfigParams',
        payload: { paramCodes },
      });
      const encryptParams = {
        url: '/whalecloud/cvbs/v1',
        belong: 'ocs',
        data: Base64.encode(JSON.stringify(payload)),
      };
      let result = yield call(qryPaymentConfirm, encryptParams);
      result = getRequestData(result);
      if (Base64.isValid(result)) {
        result = JSON.parse(Base64.decode(result));
      }
      let transactionResult = {};
      if (!isEmpty(get(result, 'ApiResponse'))) {
        notification.error({
          message: 'Error',
          description: get(result, 'ApiResponse.resultDesc'),
        });
        yield put({
          type: 'save',
          payload: {
            transactionResult,
            // BL-S-ACT-00835代表pay state的状态为E,BL-S-ACT-00834为B
            refreshState:
              get(result, 'ApiResponse.resultCode') === 'BL-S-ACT-00834' ||
              get(result, 'ApiResponse.resultCode') === 'BL-S-ACT-00835',
          },
        });
        return false;
      }
      if (!isEmpty(result)) {
        transactionResult = result;
      }
      yield put({
        type: 'save',
        payload: {
          transactionResult,
          refreshState: false,
        },
      });
      return true;
    },

    *qryConfigParams({ payload }, { call, put }) {
      const response = yield call(qryConfigParams, payload);
      const paramsList = getRequestData(response);
      const paramsObj = {};
      if (!isEmpty(paramsList)) {
        paramsList.forEach(item => {
          if (item) {
            paramsObj[item.paramCode] = item.paramValue || item.defaultValue;
          }
        });
      }

      yield put({
        type: 'save',
        payload: { configParams: paramsObj },
      });
    },

    *genCaptcha(_, { call, put }) {
      const response = yield call(genCaptcha);
      const { imgCode, imgToken } = getRequestData(response);
      yield put({
        type: 'save',
        payload: {
          imgReturn: {
            imgCode,
            imgToken,
          },
        },
      });
    },

    *validCaptcha({ payload }, { call }) {
      const response = yield call(validCaptcha, payload);
      const { returnCode } = getRequestData(response);
      if (returnCode === '0') {
        return true;
      }
      return false;
    },

    // 查询账单信息
    *qryDue({ payload }, { call, put }) {
      const encryptParams = {
        url: '/whalecloud/cvbs/v1',
        belong: 'ocs',
        data: Base64.encode(JSON.stringify(payload)),
      };
      let result = yield call(qryDue, encryptParams);
      result = getRequestData(result);
      if (Base64.isValid(result)) {
        result = JSON.parse(Base64.decode(result));
      }
      const billingInfo = result || {};
      if (!isEmpty(get(result, 'ApiResponse'))) {
        const resultCode = get(result, 'ApiResponse.resultCode');
        let description = get(result, 'ApiResponse.resultDesc');
        if (resultCode === 'BL-S-ACT-00731') {
          description = i18n('ONLINE_PAYMENT_PAYMENT_LINK_EXPIRED');
        }
        notification.error({
          message: 'Error',
          description,
        });
        return false;
      }
      yield put({
        type: 'save',
        payload: {
          billingInfo,
        },
      });
      return billingInfo;
    },
  },
  reducers: {
    save(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    reset() {
      return {
        ...initState,
      };
    },
  },
};
